/**
 * Custom hook for guest request file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchGuestRequestFiles,
  type GuestRequestFileSearchFilters,
  type GuestRequestFileSearchResponse,
} from '../api/guestRequestFileSearchApi'

/**
 * Hook for searching guest request files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with guest request file data
 */
export function useGuestRequestFileSearch(
  filters: GuestRequestFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: GuestRequestFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<GuestRequestFileSearchResponse, Error>({
    queryKey: ['guest-request-file-search', safeFilters],
    queryFn: () => searchGuestRequestFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { GuestRequestFileSearchFilters, GuestRequestFileSearchResponse }
